import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigs } from '@/api/generated/company-management-user/company-management-user'
import type { HealthInsuranceEmployeeConfigModel } from '@/api/model'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import type { ImportRow } from '../header-actions-components/HealthInsuranceImportButton'

type RowStatus = 'idle' | 'pending' | 'success' | 'error'

interface ParsedRow extends ImportRow {
  status: RowStatus
  errorMessage?: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialRows: ImportRow[]
  serviceModuleId: number
  companyId: number
  employees: { id?: string | null; firstName?: string | null; lastName?: string | null }[]
  configs: HealthInsuranceEmployeeConfigModel[]
  onSuccess?: () => void
}

export function HealthInsuranceImportDialog({ open, onOpenChange, initialRows, serviceModuleId, companyId, employees, configs, onSuccess }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'common'])
  const [pendingRows, setPendingRows] = useState<ParsedRow[]>([])
  const [isRunning, setIsRunning] = useState(false)

  const { mutateAsync } = usePatchApiCompaniesCurrentServicemodulesInsuranceServiceModuleIdEmployeeconfigs()

  const isDone = pendingRows.length > 0 && pendingRows.every(r => r.status === 'success' || r.status === 'error')
  const hasErrors = pendingRows.some(r => r.status === 'error')

  useEffect(() => {
    if (open) {
      setPendingRows(initialRows.map(r => ({ ...r, status: 'idle' })))
    }
  }, [open, initialRows])

  const handleConfirm = async () => {
    setIsRunning(true)

    for (let i = 0; i < pendingRows.length; i++) {
      const row = pendingRows[i]
      setPendingRows(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'pending' } : r))

      const employee = employees.find(e =>
        e.firstName?.trim().toLowerCase() === row.firstName.toLowerCase() &&
        e.lastName?.trim().toLowerCase() === row.lastName.toLowerCase()
      )

      if (!employee?.id) {
        setPendingRows(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', errorMessage: t('insurance.import.employeeNotFound', { name: `${row.firstName} ${row.lastName}` }) } : r
        ))
        continue
      }

      try {
        const config = configs.find(c => c.employeeUserId === employee.id)
        await mutateAsync({
          serviceModuleId,
          data: {
            id: config?.id ?? 0,
            employeeUserId: employee.id,
            companyId,
            serviceModuleId,
            healthInsuranceNumber: row.healthInsuranceNumber,
          },
        })
        setPendingRows(prev => prev.map((r, idx) => idx === i ? { ...r, status: 'success' } : r))
      } catch (error: any) {
        const message = error?.response?.data?.message ?? t('insurance.import.error')
        setPendingRows(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', errorMessage: message } : r
        ))
      }
    }

    setIsRunning(false)

    const allErrors = pendingRows.every(r => r.status === 'error')
    if (allErrors) {
      return
    }

    onSuccess?.()
  }

  const handleCancel = () => {
    if (isRunning) return
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel() }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('insurance.import.title')}</DialogTitle>
        </DialogHeader>

        <div className="max-h-80 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('columns.firstName', { ns: 'common' })}</TableHead>
                <TableHead>{t('columns.lastName', { ns: 'common' })}</TableHead>
                <TableHead>{t('insurance.overview.insuranceNumber')}</TableHead>
                <TableHead className="w-8">{t('insurance.import.status')}</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingRows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.firstName || '—'}</TableCell>
                  <TableCell>{row.lastName || '—'}</TableCell>
                  <TableCell>{row.healthInsuranceNumber || '—'}</TableCell>
                  <TableCell>
                    {row.status === 'pending' && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                    {row.status === 'success' && <CheckCircle2 className="size-4 text-emerald-500" />}
                    {row.status === 'error' && (
                      <div className="flex items-center gap-1">
                        <XCircle className="size-4 text-red-500 shrink-0" />
                        {row.errorMessage && <span className="text-xs text-red-500">{row.errorMessage}</span>}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          {!isRunning && !isDone && (
            <Button variant="outline" onClick={handleCancel}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
          )}
          {!isDone && (
            <Button onClick={handleConfirm} disabled={isRunning}>
              {isRunning && <Loader2 className="size-4 animate-spin" />}
              {t('insurance.import.confirm')}
            </Button>
          )}
          {isDone && (
            <Button onClick={handleCancel} variant={hasErrors ? 'outline' : 'default'}>
              {t('buttons.close', { ns: 'common' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
